/**
 * Locale utilities for bilingual EN/AR support.
 *
 * Active UI locale always comes from the URL `/:locale` segment.
 * Opening a locale URL also persists to cookie + profile.privateData.locale
 * (emails read the profile field, not the URL).
 * Preferences still drive `/` redirects. Guests without a cookie fall back to
 * the browser language (Accept-Language / navigator.language).
 */

export const SUPPORTED_LOCALES = ['en', 'ar'];
export const DEFAULT_LOCALE = 'en';
export const LOCALE_COOKIE_NAME = 'sw_locale';
export const LOCALE_STORAGE_KEY = 'sw_locale';

/**
 * When true, `/` redirect may use Accept-Language / navigator.language
 * if the user has no cookie or profile preference.
 */
export const ENABLE_BROWSER_LOCALE_DETECTION = true;

const LOCALE_PATH_PREFIX = /^\/(en|ar)(?=\/|$)/;

/** In-memory fallback used when path helpers run outside React (SSR/link builders). */
let activeLocale = DEFAULT_LOCALE;

export const isSupportedLocale = locale => SUPPORTED_LOCALES.includes(locale);

export const normalizeLocale = locale => {
  if (!locale || typeof locale !== 'string') {
    return null;
  }
  const base = locale.toLowerCase().split(/[-_]/)[0];
  return isSupportedLocale(base) ? base : null;
};

export const getActiveLocale = () => activeLocale;

export const setActiveLocale = locale => {
  const normalized = normalizeLocale(locale) || DEFAULT_LOCALE;
  activeLocale = normalized;
  return activeLocale;
};

export const localeFromPathname = pathname => {
  if (!pathname || typeof pathname !== 'string') {
    return null;
  }
  const match = pathname.match(LOCALE_PATH_PREFIX);
  return match ? match[1] : null;
};

export const stripLocaleFromPathname = pathname => {
  if (!pathname || typeof pathname !== 'string') {
    return pathname || '/';
  }
  const stripped = pathname.replace(LOCALE_PATH_PREFIX, '');
  return stripped === '' ? '/' : stripped;
};

/**
 * Unprefixed /u and /l share URLs: language is optional, so visitors are sent
 * to their preferred locale instead of a hard-coded English 301.
 */
export const isLocaleOptionalSharePath = pathname => {
  if (!pathname || typeof pathname !== 'string') {
    return false;
  }
  return (
    pathname === '/u' ||
    pathname.startsWith('/u/') ||
    pathname === '/l' ||
    pathname.startsWith('/l/')
  );
};

/**
 * Swap the locale prefix on a pathname, preserving the rest of the path.
 * If the path has no locale prefix, one is added.
 */
export const replaceLocaleInPathname = (pathname, nextLocale) => {
  const locale = normalizeLocale(nextLocale) || DEFAULT_LOCALE;
  const withoutLocale = stripLocaleFromPathname(pathname || '/');
  if (withoutLocale === '/') {
    return `/${locale}`;
  }
  return `/${locale}${withoutLocale}`;
};

export const detectBrowserLocale = languageList => {
  const languages =
    languageList ||
    (typeof navigator !== 'undefined'
      ? navigator.languages || (navigator.language ? [navigator.language] : [])
      : []);

  for (const lang of languages) {
    const normalized = normalizeLocale(lang);
    if (normalized) {
      return normalized;
    }
  }
  return DEFAULT_LOCALE;
};

export const getLocaleFromCookie = cookieHeader => {
  if (!cookieHeader || typeof cookieHeader !== 'string') {
    return null;
  }
  const parts = cookieHeader.split(';');
  for (const part of parts) {
    const [rawKey, ...rest] = part.trim().split('=');
    if (rawKey === LOCALE_COOKIE_NAME) {
      return normalizeLocale(decodeURIComponent(rest.join('=')));
    }
  }
  return null;
};

export const getStoredLocale = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const fromStorage = normalizeLocale(window.localStorage?.getItem(LOCALE_STORAGE_KEY));
    if (fromStorage) {
      return fromStorage;
    }
  } catch (e) {
    // ignore storage access errors
  }
  return getLocaleFromCookie(typeof document !== 'undefined' ? document.cookie : '');
};

export const setStoredLocale = locale => {
  const normalized = normalizeLocale(locale) || DEFAULT_LOCALE;
  setActiveLocale(normalized);

  if (typeof document !== 'undefined') {
    const maxAge = 60 * 60 * 24 * 365;
    document.cookie = `${LOCALE_COOKIE_NAME}=${encodeURIComponent(
      normalized
    )}; path=/; max-age=${maxAge}; SameSite=Lax`;
  }

  if (typeof window !== 'undefined') {
    try {
      window.localStorage?.setItem(LOCALE_STORAGE_KEY, normalized);
    } catch (e) {
      // ignore storage access errors
    }
  }

  return normalized;
};

/** Keep a supported locale; drop unknown values. */
export const clampLocaleForAccess = locale => normalizeLocale(locale);

/**
 * Locale to persist on cookie/profile.
 * URL wins (opening `/ar/l/...` is an explicit language choice), then profile, then cookie.
 * Unprefixed paths leave `urlLocale` null so we do not overwrite an Arabic profile with English.
 */
export const resolveLocaleToPersist = ({ urlLocale, profileLocale, storedLocale } = {}) => {
  const fromUrl = clampLocaleForAccess(urlLocale);
  if (fromUrl) {
    return fromUrl;
  }
  return clampLocaleForAccess(profileLocale) || clampLocaleForAccess(storedLocale) || null;
};

export const shouldWriteProfileLocale = ({ urlLocale, profileLocale, storedLocale } = {}) => {
  const next = resolveLocaleToPersist({
    urlLocale,
    profileLocale,
    storedLocale,
  });
  if (!next) {
    return false;
  }
  const current = normalizeLocale(profileLocale);
  if (urlLocale && clampLocaleForAccess(urlLocale) && next !== current) {
    return true;
  }
  if (!current && clampLocaleForAccess(storedLocale)) {
    return true;
  }
  return false;
};

/**
 * Resolve locale for `/` (and similar) redirects.
 * Priority: profile → stored preference → (optional) browser → default English.
 */
export const resolveLocaleForRedirect = ({
  profileLocale,
  storedLocale,
  browserLocale,
  enableBrowserDetection = ENABLE_BROWSER_LOCALE_DETECTION,
} = {}) => {
  const fromProfile = clampLocaleForAccess(profileLocale);
  if (fromProfile) {
    return fromProfile;
  }
  const fromStored = clampLocaleForAccess(storedLocale);
  if (fromStored) {
    return fromStored;
  }
  if (enableBrowserDetection) {
    const fromBrowser =
      clampLocaleForAccess(browserLocale) || clampLocaleForAccess(detectBrowserLocale());
    if (fromBrowser) {
      return fromBrowser;
    }
  }
  return DEFAULT_LOCALE;
};

export const isRtlLocale = locale => normalizeLocale(locale) === 'ar';

export const htmlLangForLocale = locale => {
  const normalized = normalizeLocale(locale) || DEFAULT_LOCALE;
  return normalized === 'ar' ? 'ar' : 'en';
};

export const openGraphLocaleFor = locale => {
  const normalized = normalizeLocale(locale) || DEFAULT_LOCALE;
  return normalized === 'ar' ? 'ar_SA' : 'en_US';
};

export const alternateLocale = locale =>
  (normalizeLocale(locale) || DEFAULT_LOCALE) === 'ar' ? 'en' : 'ar';

/**
 * Paths that must remain unprefixed (IdP / Sharetribe API expectations, assets, APIs).
 */
export const UNPREFIXED_PATH_PREFIXES = [
  '/api',
  '/static',
  '/sitemap-',
  '/robots.txt',
  '/site.webmanifest',
  '/sw.js',
  '/pwa-install.bundle.js',
  '/_status.json',
  '/.well-known',
  '/favicon.ico',
  '/login',
  '/reset-password',
  '/verify-email',
  '/preview',
  '/practitioners',
  '/seekers',
  '/g',
];

/** Root public files (e.g. /pwa-install.bundle.js) must not be locale-prefixed. */
const ROOT_STATIC_FILE = /^\/[^/]+\.[a-zA-Z0-9.]+$/;

export const shouldSkipLocalePrefix = pathname => {
  if (!pathname || pathname === '/') {
    return false;
  }
  if (ROOT_STATIC_FILE.test(pathname)) {
    return true;
  }
  return UNPREFIXED_PATH_PREFIXES.some(prefix => {
    if (prefix.endsWith('-')) {
      return pathname.startsWith(prefix);
    }
    if (prefix.includes('.')) {
      return pathname === prefix || pathname.startsWith(`${prefix}?`);
    }
    return (
      pathname === prefix || pathname.startsWith(`${prefix}/`) || pathname.startsWith(`${prefix}?`)
    );
  });
};

export const messagesForLocale = (locale, { enMessages, arMessages, hostedTranslations = {} }) => {
  const normalized = normalizeLocale(locale) || DEFAULT_LOCALE;
  if (normalized === 'ar') {
    // Do not merge English hosted Console overrides onto Arabic.
    return { ...enMessages, ...arMessages };
  }
  return { ...enMessages, ...hostedTranslations };
};
